import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { InfoCircleIcon } from 'foundations-assets/icons/components/InfoCircleIcon';
import { UpIcon } from 'foundations-assets/icons/components/UpIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { Link } from 'foundations-components/transitional/components/Link';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import styled from 'foundations-theming/utils';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { SingleSkeletonLoader } from '../loading/SingleSkeletonLoader';
import { AgentPackCard } from './AgentPackCard';
import { buildAgentPacksTotalDisplay } from './agentPacksHelpers';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SECTION_INLINE_PADDING = '600';
const StyledCardsRow = styled.div.withConfig({
  displayName: "AgentPacksSection__StyledCardsRow"
})(({
  theme
}) => ({
  display: 'flex',
  gap: theme.space[200],
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
  scrollSnapType: 'x mandatory',
  marginInline: `-${theme.space[SECTION_INLINE_PADDING]}`,
  paddingInline: theme.space[SECTION_INLINE_PADDING],
  scrollPaddingInlineStart: theme.space[SECTION_INLINE_PADDING],
  paddingTop: theme.space[300]
}));
const StyledOwnedCreditsNotice = styled.div.withConfig({
  displayName: "AgentPacksSection__StyledOwnedCreditsNotice"
})(({
  theme
}) => ({
  borderWidth: theme.borderWidth[100],
  borderStyle: 'solid',
  borderColor: theme.color['border-core-default'],
  borderRadius: theme.borderRadius[400],
  paddingBlock: theme.space[300],
  paddingInline: theme.space[400]
}));
const StyledBoldText = styled.span.withConfig({
  displayName: "AgentPacksSection__StyledBoldText"
})({
  fontWeight: 700
});
const StyledCustomCost = styled.div.withConfig({
  displayName: "AgentPacksSection__StyledCustomCost"
})(({
  $hideValue
}) => ({
  position: 'relative',
  '& input': $hideValue ? {
    color: 'transparent'
  } : {}
}));
const StyledCustomCostSkeleton = styled.div.withConfig({
  displayName: "AgentPacksSection__StyledCustomCostSkeleton"
})(({
  theme
}) => ({
  position: 'absolute',
  insetBlock: 0,
  insetInlineStart: theme.space[300],
  display: 'flex',
  alignItems: 'center',
  pointerEvents: 'none'
}));
export const AgentPacksSection = ({
  packs,
  includedCredits,
  currentCredits,
  selectedPackId,
  currencyCode,
  initiallyExpanded = false,
  showAdditionalPriceSign = false,
  collapsedShowsCurrentOnly = false,
  additionalOwnedCredits,
  onPackSelect,
  onViewRateDetails,
  onTooltipOpen,
  onToggleExpand,
  viewRateDetailsUrl,
  customMonthlyCost,
  customCredits,
  customStepSize,
  customMaxMonthlyCost,
  customDisabled,
  isPriceLoading = false,
  onCustomAmountStep,
  onCustomAmountCommit
}) => {
  var _packs$find$label, _packs$find, _packs$find2;
  const groupName = useId();
  const collapsibleId = useId();
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);

  /* The portal's current total credits stand in for the plan's included
   * credits as the summary base and the included option's figure when the
   * caller supplies them (the HSOCV2 interstitial); otherwise the included
   * credits are the base (the CAB bundle page). */
  const baseCredits = currentCredits !== null && currentCredits !== void 0 ? currentCredits : includedCredits;

  /* Upgrade summary: reads "Total", mirrors the selection when collapsed, and folds owned credits into the base so the total is built additively (kept local so the custom stepper updates instantly). */
  const isUpgradeSummary = additionalOwnedCredits !== undefined;
  const totalBaseCredits = baseCredits + (additionalOwnedCredits !== null && additionalOwnedCredits !== void 0 ? additionalOwnedCredits : 0);
  const collapsedUsesCurrentOnly = collapsedShowsCurrentOnly && !isUpgradeSummary;
  const handleToggle = useCallback(() => {
    const next = !isExpanded;
    setIsExpanded(next);
    onToggleExpand === null || onToggleExpand === void 0 || onToggleExpand(next);
  }, [isExpanded, onToggleExpand]);
  const totalDisplay = useMemo(() => buildAgentPacksTotalDisplay({
    packs,
    selectedPackId,
    includedCredits: totalBaseCredits,
    customMonthlyCost,
    customCredits
  }), [packs, selectedPackId, totalBaseCredits, customMonthlyCost, customCredits]);

  /* The summary's baseline heading mirrors whatever label the caller gave the
   * included option ("Included" for CAB, "Current" for HSOCV2), falling back to
   * the shared "Included" message if no included option is present. */
  const baselineHeading = (_packs$find$label = (_packs$find = packs.find(p => p.type === 'included')) === null || _packs$find === void 0 ? void 0 : _packs$find.label) !== null && _packs$find$label !== void 0 ? _packs$find$label : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "agentPacksComponents.packLabels.included"
  });
  const isCustomSelected = ((_packs$find2 = packs.find(p => p.id === selectedPackId)) === null || _packs$find2 === void 0 ? void 0 : _packs$find2.type) === 'custom';
  const recommendedPack = packs.find(p => p.type === 'paid' && p.isRecommended === true);

  /*
   * Distinguishes typing from +/- stepping. StepperInput.handleStep fires
   * onChange then onStep synchronously, so a step ends with hasTyped=false.
   * This lets us skip the commit on the transient blur a +/- button click
   * triggers (focus briefly leaves the input for the button) — otherwise the
   * blur would snap the pre-step value and overwrite the step with one less.
   */
  const hasTypedRef = useRef(false);
  const [isCustomFocused, setIsCustomFocused] = useState(false);
  const isCustomCostLoading = isPriceLoading && !isCustomFocused;
  const handleCustomFocus = useCallback(() => setIsCustomFocused(true), []);
  const handleCustomBlur = useCallback(() => setIsCustomFocused(false), []);
  const handleStepperChange = useCallback(() => {
    hasTypedRef.current = true;
  }, []);
  const handleStepperStep = useCallback(evt => {
    hasTypedRef.current = false;
    const parsed = Number(evt.target.value);
    if (Number.isNaN(parsed)) {
      return;
    }
    onCustomAmountStep(parsed);
  }, [onCustomAmountStep]);
  const handleStepperBlur = useCallback(evt => {
    // Only a typed value needs snapping; stepping already lands on a valid
    // increment. Skipping non-typed blurs avoids the +/- button clobber.
    if (!hasTypedRef.current) {
      return;
    }
    hasTypedRef.current = false;
    const parsed = Number(evt.target.value);
    if (Number.isNaN(parsed)) {
      return;
    }
    onCustomAmountCommit(parsed);
  }, [onCustomAmountCommit]);
  const handleStepperKeyDown = useCallback(evt => {
    if (evt.key === 'Enter') {
      evt.currentTarget.blur();
    }
  }, []);

  /*
   * The selected-pack summary appears in two places at different sizes: a
   * compact `heading-50` under the header when collapsed, and a prominent
   * `heading-300` below the cards when expanded.
   */
  const renderIncludedSummary = headingTypography => {
    let headingContent;
    if (totalDisplay.type === 'included') {
      headingContent = isUpgradeSummary ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "agentPacksComponents.total"
      }) : baselineHeading;
    } else {
      const price = /*#__PURE__*/_jsx(FormattedIntlCurrency, {
        value: totalDisplay.monthlyCost,
        currencyCode: currencyCode,
        trailingZeroDisplay: "stripIfInteger"
      });
      if (showAdditionalPriceSign) {
        headingContent = /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "agentPacksComponents.additionalPricePerMonth",
          options: {
            price
          }
        });
      } else {
        headingContent = /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "agentPacksComponents.pricePerMonth",
          options: {
            price
          }
        });
      }
    }
    return /*#__PURE__*/_jsxs(View, {
      children: [isPriceLoading && totalDisplay.type === 'priced' ? /*#__PURE__*/_jsx(SingleSkeletonLoader, {
        height: 14,
        width: "96px"
      }) : /*#__PURE__*/_jsx(Text, {
        typography: headingTypography,
        children: headingContent
      }), /*#__PURE__*/_jsx(Text, {
        typography: "body-100",
        color: "text-core-subtle",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "agentPacksComponents.totalCredits",
          options: {
            credits: /*#__PURE__*/_jsx(FormattedNumber, {
              value: totalDisplay.credits
            })
          }
        })
      })]
    });
  };

  /* A selection-independent summary: the base credits (the portal's current
   * total) with the included/"current" label and no price. */
  const renderCurrentSummary = headingTypography => /*#__PURE__*/_jsxs(View, {
    children: [/*#__PURE__*/_jsx(Text, {
      typography: headingTypography,
      children: baselineHeading
    }), /*#__PURE__*/_jsx(Text, {
      typography: "body-100",
      color: "text-core-subtle",
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "agentPacksComponents.totalCredits",
        options: {
          credits: /*#__PURE__*/_jsx(FormattedNumber, {
            value: baseCredits
          })
        }
      })
    })]
  });
  return /*#__PURE__*/_jsx(CardWrapper, {
    inlineSize: "100%",
    padding: SECTION_INLINE_PADDING,
    "data-test-id": "agent-packs-section",
    children: /*#__PURE__*/_jsxs(Flex, {
      as: "section",
      flexDirection: "column",
      gap: "400",
      children: [/*#__PURE__*/_jsxs(Flex, {
        justifyContent: "space-between",
        alignItems: isExpanded ? 'start' : 'center',
        gap: "300",
        children: [/*#__PURE__*/_jsxs(View, {
          children: [/*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            gap: "200",
            children: [/*#__PURE__*/_jsx(Text, {
              as: "h3",
              typography: "heading-200",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "agentPacksComponents.heading"
              })
            }), /*#__PURE__*/_jsx(Tooltip, {
              use: "longform",
              headingText: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "agentPacksComponents.tooltipHeading"
              }),
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "agentPacksComponents.tooltipText"
              }),
              placement: "top",
              onOpenChange: onTooltipOpen,
              children: /*#__PURE__*/_jsx(View, {
                as: "span",
                role: "img",
                "aria-label": unescapedText('agentPacksComponents.tooltipAriaLabel'),
                children: /*#__PURE__*/_jsx(InfoCircleIcon, {})
              })
            })]
          }), !isExpanded && /*#__PURE__*/_jsx(View, {
            paddingBlockStart: "100",
            children: collapsedUsesCurrentOnly ? renderCurrentSummary('heading-50') : renderIncludedSummary('heading-50')
          }), /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            color: "text-core-subtle",
            paddingBlockStart: isExpanded ? '300' : '100',
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "agentPacksComponents.description"
            })
          })]
        }), /*#__PURE__*/_jsx(Button, {
          use: "transparent",
          size: "small",
          borderWidth: "100",
          borderColor: "border-core-default",
          borderRadius: "full",
          paddingBlock: "100",
          paddingInlineStart: "400",
          paddingInlineEnd: "300",
          onClick: handleToggle,
          "aria-expanded": isExpanded,
          "aria-controls": isExpanded ? collapsibleId : undefined,
          "data-test-id": "agent-packs-toggle",
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            gap: "100",
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "body-100",
              color: "text-core-default",
              children: isExpanded ? /*#__PURE__*/_jsx(FormattedMessage, {
                message: "agentPacksComponents.collapse"
              }) : /*#__PURE__*/_jsx(FormattedMessage, {
                message: "agentPacksComponents.expand"
              })
            }), isExpanded ? /*#__PURE__*/_jsx(UpIcon, {
              color: "icon-core-default"
            }) : /*#__PURE__*/_jsx(DownIcon, {
              color: "icon-core-default"
            })]
          })
        })]
      }), isExpanded && /*#__PURE__*/_jsxs(Flex, {
        as: "div",
        id: collapsibleId,
        flexDirection: "column",
        gap: "400",
        children: [isUpgradeSummary && (additionalOwnedCredits !== null && additionalOwnedCredits !== void 0 ? additionalOwnedCredits : 0) > 0 && /*#__PURE__*/_jsx(StyledOwnedCreditsNotice, {
          "data-test-id": "agent-packs-owned-credits-notice",
          children: /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            color: "text-core-default",
            children: /*#__PURE__*/_jsx(FormattedReactMessage, {
              message: "agentPacksComponents.additionalCreditsNotice",
              options: {
                ownedCredits: /*#__PURE__*/_jsx(StyledBoldText, {
                  children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                    message: "agentPacksComponents.additionalCreditsNoticeAmount",
                    options: {
                      credits: /*#__PURE__*/_jsx(FormattedNumber, {
                        value: additionalOwnedCredits
                      })
                    }
                  })
                })
              }
            })
          })
        }), /*#__PURE__*/_jsxs(View, {
          children: [/*#__PURE__*/_jsx(StyledCardsRow, {
            role: "radiogroup",
            "aria-label": unescapedText('agentPacksComponents.sectionLabel'),
            children: packs.map(pack => /*#__PURE__*/_jsx(AgentPackCard, {
              pack: pack,
              isSelected: pack.id === selectedPackId,
              baselineCredits: baseCredits,
              currencyCode: currencyCode,
              groupName: groupName,
              isPriceLoading: isPriceLoading,
              onSelect: onPackSelect
            }, pack.id))
          }), isCustomSelected && /*#__PURE__*/_jsx(Text, {
            typography: "detail-100",
            color: "text-core-subtle",
            paddingBlockStart: "300",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "agentPacksComponents.recommendationDisclaimerOther"
            })
          }), !isCustomSelected && recommendedPack && /*#__PURE__*/_jsx(Text, {
            typography: "detail-100",
            color: "text-core-subtle",
            paddingBlockStart: "300",
            children: /*#__PURE__*/_jsx(FormattedReactMessage, {
              message: "agentPacksComponents.recommendationDisclaimer",
              options: {
                plan: recommendedPack.label
              }
            })
          })]
        }), isCustomSelected && /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "300",
          children: [/*#__PURE__*/_jsxs(StyledCustomCost, {
            $hideValue: isCustomCostLoading,
            onFocus: handleCustomFocus,
            onBlur: handleCustomBlur,
            children: [/*#__PURE__*/_jsx(StepperInput, {
              value: customMonthlyCost,
              stepSize: customStepSize,
              min: 0,
              max: customMaxMonthlyCost,
              currency: currencyCode,
              disabled: customDisabled,
              onChange: handleStepperChange,
              onStep: handleStepperStep,
              onBlur: handleStepperBlur,
              onKeyDown: handleStepperKeyDown,
              "aria-label": unescapedText('agentPacksComponents.customAmountAriaLabel'),
              maxInlineSize: "140px",
              "data-test-id": "agent-packs-custom-amount-stepper"
            }), isCustomCostLoading && /*#__PURE__*/_jsx(StyledCustomCostSkeleton, {
              children: /*#__PURE__*/_jsx(SingleSkeletonLoader, {
                height: 14,
                width: "64px",
                "data-test-id": "agent-packs-custom-cost-skeleton"
              })
            })]
          }), customCredits > 0 && /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            color: "text-core-subtle",
            children: /*#__PURE__*/_jsx(FormattedReactMessage, {
              message: "agentPacksComponents.credits",
              options: {
                credits: /*#__PURE__*/_jsx(FormattedNumber, {
                  value: customCredits
                })
              }
            })
          })]
        }), renderIncludedSummary('heading-300'), viewRateDetailsUrl && /*#__PURE__*/_jsx(View, {
          paddingBlockStart: "100",
          children: /*#__PURE__*/_jsx(Link, {
            href: viewRateDetailsUrl,
            external: true,
            hideExternalLinkIcon: true,
            underline: true,
            onClick: onViewRateDetails,
            "data-test-id": "view-rate-details-link",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "agentPacksComponents.viewRateDetails"
            })
          })
        })]
      })]
    })
  });
};